/**
 * Voyage Formatters Composable
 * Provides formatting utility functions for voyage data display
 */

import { fromApiDatetime, DISPLAY_DATETIME } from '@/util/apiDatetime';
import { toDuration } from '@/util/date';

export function useVoyageFormatters() {
  /**
   * Format a date string to Indonesian format (DD MMMM YYYY, HH:mm:ss)
   * @param {string} dateString - Date string to format
   * @returns {string} Formatted date string
   */
  const formatIndonesianDate = (dateString) => fromApiDatetime(dateString).format(DISPLAY_DATETIME);

  /**
   * Format voyage duration from start and end times
   * @param {string} startTime - Start time string
   * @param {string} endTime - End time string
   * @returns {string} Formatted duration string or 'N/A'
   */
  const formatVoyageDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    return toDuration(fromApiDatetime(endTime).unix() - fromApiDatetime(startTime).unix());
  };

  /**
   * Get CSS class for sea state based on value (text color only)
   * @param {string} seaState - Sea state value
   * @returns {string} CSS class name for text color
   */
  const getSeaStateTextClass = (seaState) => {
    if (!seaState) return 'text-secondary';
    const state = seaState.toUpperCase();
    if (state === 'CALM') return 'text-success';
    if (state === 'SLIGHT') return 'text-success';
    if (state === 'MODERATE') return 'text-warning';
    if (state === 'ROUGH') return 'text-warning';
    if (state === 'VERY ROUGH') return 'text-danger';
    if (state === 'HIGH') return 'text-danger';
    if (state === 'VERY HIGH') return 'text-danger';
    return 'text-secondary';
  };

  return {
    formatIndonesianDate,
    formatVoyageDuration,
    getSeaStateTextClass,
  };
}

export default useVoyageFormatters;
